import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, Power, ChevronRight } from 'lucide-react';
import { VerneMark } from '@verne/brand';
import { ThemeToggle } from '@/components/layout/theme-toggle';
import { UserMenu } from '@/components/auth/UserMenu';
import { OrgSelector } from '@/components/nav/OrgSelector';
import { RobotSelector } from '@/components/nav/RobotSelector';
import {
  ContactPopover,
  StatusLine,
  PositionLine,
  BatteryLine,
  StorageModeLine,
} from '@/components/layout/dashboard-sidebar';
import { PowerOffOverlay } from '@/components/system/power-off-overlay';
import { useEffectiveOrgName } from '@/stores/useOrgStore';
import { ROUTES } from '@/lib/routes';

interface Props {
  onClose: () => void;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-3 border-b border-border/40">
      <span className="text-sm text-muted">{label}</span>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

export function MobileHamburger({ onClose }: Props) {
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  const isLoggedIn = useEffectiveOrgName() != null;
  const [powerOffOpen, setPowerOffOpen] = useState(false);

  const onEventLog = pathname === ROUTES.EVENT_LOG;
  const otherTab = onEventLog
    ? { label: 'Dashboard', path: ROUTES.DASHBOARD }
    : { label: 'Event Log', path: ROUTES.EVENT_LOG };

  return (
    <div className="h-full w-full bg-background flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
        <span className="text-base font-semibold text-foreground">Menu</span>
        <button
          onClick={onClose}
          className="p-2 rounded-lg text-muted hover:text-foreground hover:bg-surface-raised transition-colors"
          aria-label="Close menu"
        >
          <X size={22} />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
        <div className="flex items-stretch gap-2 mb-4">
          <ContactPopover
            placement="below"
            trigger={({ onClick }) => (
              <button
                onClick={onClick}
                className="flex-1 rounded-md border border-border bg-surface/40 px-4 py-3 text-base font-semibold text-foreground hover:bg-surface-strong transition flex items-center justify-center gap-2"
              >
                <VerneMark aria-hidden className="h-5 w-auto" />
                Help
              </button>
            )}
          />
          <button
            onClick={() => setPowerOffOpen(true)}
            title="Power off the robot"
            aria-label="Power off the robot"
            className="flex-1 rounded-md border border-border bg-surface/40 px-4 py-3 text-foreground hover:bg-red-500/10 hover:border-red-500/50 hover:text-red-500 transition flex items-center justify-center gap-2"
          >
            <Power size={18} />
            <span className="text-base font-semibold">Power Off</span>
          </button>
        </div>

        <div className="rounded-md border border-border bg-surface/40 p-3 flex flex-col gap-2 mb-4">
          <span className="text-[11px] uppercase tracking-wider text-muted">Robot Status</span>
          <div className="flex flex-col gap-1.5">
            <StatusLine />
            <PositionLine />
            <BatteryLine />
            <StorageModeLine />
          </div>
        </div>

        <Row label="Theme"><ThemeToggle /></Row>
        <Row label="Profile"><UserMenu /></Row>
        {isLoggedIn && <Row label="Organization"><OrgSelector /></Row>}
        <Row label="Robot"><RobotSelector /></Row>

        {isLoggedIn && (
          <button
            onClick={() => navigate(otherTab.path)}
            className="mt-4 w-full flex items-center justify-between rounded-md border border-border bg-surface/40 px-4 py-3 text-base font-semibold text-foreground hover:bg-surface-strong transition"
          >
            <span>Go to {otherTab.label}</span>
            <ChevronRight size={18} className="text-muted" />
          </button>
        )}
      </div>

      {powerOffOpen && <PowerOffOverlay onCancel={() => setPowerOffOpen(false)} />}
    </div>
  );
}
